import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { useTeamOverview } from '@/hooks/useTeamOverview';
import { useEmploymentTypes, EmploymentTypeBadge } from '@/components/EmploymentTypeSelector';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EditWorkingDaysDialog } from '@/components/attendance/EditWorkingDaysDialog';
import {
  Calendar,
  Users,
  Search,
  UserCheck,
  UserX,
  Clock,
  CalendarDays,
  Settings,
  ChevronRight,
  ArrowRight,
  Briefcase
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Consistent animation config
const EASE = [0.25, 0.46, 0.45, 0.94];

// StatPill component matching other attendance pages
const StatPill = ({ icon: Icon, label, value, color = "slate", delay = 0 }) => {
  const colorClasses = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: EASE }}
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${colorClasses[color]} transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default`}
    >
      <div className="p-2 rounded-xl bg-white/80 shadow-sm">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-xs font-medium opacity-70">{label}</p>
        <p className="text-lg font-bold -mt-0.5">{value}</p>
      </div>
    </motion.div>
  );
};

// Team member row component
const TeamMemberRow = ({ member, employmentType, getUsagePercentage, getUsageColor, canManageAttendance, onEdit, onView, delay = 0 }) => {
  const usagePercent = getUsagePercentage(member);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay, ease: EASE }}
      onClick={() => onView(member.id)}
      className="group relative bg-white rounded-xl border border-slate-200/80 p-4 pl-5 transition-all duration-300 hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:border-slate-300/80 cursor-pointer"
    >
      {/* Status accent bar */}
      <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${member.on_leave_today ? 'bg-amber-500' : 'bg-emerald-500'}`} />

      <div className="flex items-center gap-4">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
          <span className="text-white font-semibold text-sm">
            {member.first_name[0]}{member.last_name[0]}
          </span>
        </div>

        {/* Name & Email */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[15px] font-semibold text-slate-800">
              {member.first_name} {member.last_name}
            </p>
            {employmentType && (
              <EmploymentTypeBadge type={employmentType} size="sm" showIcon={false} />
            )}
          </div>
          <p className="text-sm text-slate-500 truncate">{member.email}</p>
        </div>

        {/* Status Badge */}
        <div className="hidden sm:block">
          {member.on_leave_today ? (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
              On Leave
            </span>
          ) : (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              Available
            </span>
          )}
        </div>

        {/* Leave Stats */}
        <div className="hidden md:flex items-center gap-6 text-sm">
          <div className="text-center">
            <p className="text-xs text-slate-500">Entitled</p>
            <p className="font-semibold text-slate-800">{member.entitled || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500">Taken</p>
            <p className={cn("font-semibold", getUsageColor(usagePercent))}>{member.taken || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500">Left</p>
            <p className="font-bold text-slate-800">{member.remaining || 0}</p>
          </div>
        </div>

        {/* Pending Badge */}
        {member.pending_requests > 0 && (
          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            {member.pending_requests} pending
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          {canManageAttendance && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(member);
              }}
              className="p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title="Edit working days"
            >
              <Settings className="h-4 w-4" />
            </button>
          )}
          <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
        </div>
      </div>
    </motion.div>
  );
};

// Insight card for leave previews
const InsightCard = ({ title, subtitle, icon: Icon, iconColor, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: EASE }}
    className="rounded-2xl bg-white border border-slate-200 p-6"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className={cn("p-2 rounded-xl", iconColor)}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h3 className="font-semibold text-slate-800">{title}</h3>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
    </div>
    {children}
  </motion.div>
);

// Leave request preview item
const LeavePreviewItem = ({ request, getLeaveTypeColor, getLeaveTypeAbbr, showDaysUntil = false }) => {
  const start = new Date(request.start_date);
  const end = new Date(request.end_date);

  return (
    <div className="group relative bg-white rounded-xl border border-slate-200/80 p-3 pl-4 transition-all duration-200 hover:shadow-sm hover:border-slate-300/80">
      <div className={`absolute left-0 top-2 bottom-2 w-1 rounded-full ${getLeaveTypeAccent(request.leave_type)}`} />

      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
          <span className="text-white font-semibold text-xs">
            {request.first_name?.[0]}{request.last_name?.[0]}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">
            {request.first_name} {request.last_name}
          </p>
          <p className="text-xs text-slate-500">
            {start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          </p>
        </div>
        <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold", getLeaveTypeColor(request.leave_type, request.user_id))}>
          {getLeaveTypeAbbr(request.leave_type, request.user_id)}
        </span>
        {showDaysUntil && request.daysUntil !== undefined && (
          <span className="text-xs text-slate-400">
            in {request.daysUntil}d
          </span>
        )}
      </div>
    </div>
  );
};

// Normalise leave type name to a key for colour lookups
const normaliseLeaveType = (leaveType) => {
  if (!leaveType) return 'other';
  return leaveType.toLowerCase().replace(/\s+/g, '_');
};

// Leave type accent colors for bars
const LEAVE_ACCENT_MAP = {
  annual_leave: 'bg-emerald-500',
  sick_leave: 'bg-rose-500',
  personal_leave: 'bg-violet-500',
  work_from_home: 'bg-sky-500',
  training: 'bg-amber-500',
  maternity_leave: 'bg-pink-500',
  paternity_leave: 'bg-indigo-500',
  parental_leave: 'bg-indigo-500',
  unpaid_leave: 'bg-slate-500',
  compassionate_leave: 'bg-purple-500',
  bereavement_leave: 'bg-purple-500',
  birthday_leave: 'bg-pink-500',
  birthday: 'bg-pink-500',
  christmas_shutdown: 'bg-amber-500',
  christmas_shut_down: 'bg-amber-500',
  study_leave: 'bg-sky-500',
  unavailable_to_work: 'bg-slate-500',
};

const getLeaveTypeAccent = (leaveType) => {
  const key = normaliseLeaveType(leaveType);
  return LEAVE_ACCENT_MAP[key] || 'bg-slate-400';
};

export const TeamOverviewPage = () => {
  const navigate = useNavigate();
  const { userProfile, company, can, authReady } = useAuth();
  const canManageAttendance = can('attendance.manage');

  usePageTitle('Team Overview');

  // SECURITY: Redirect users without attendance management permission
  useEffect(() => {
    if (authReady && !canManageAttendance) {
      toast.error('You do not have permission to view team overview');
      navigate('/attendance');
    }
  }, [authReady, canManageAttendance, navigate]);

  // Local UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [editingMember, setEditingMember] = useState(null);

  // Employment types
  const { types: employmentTypes, getTypeById } = useEmploymentTypes(company?.id);

  const {
    loading,
    teamMembers,
    stats,
    offThisWeek,
    upcomingLeave,
    thisMonthLeave,
    nextMonthLeave,
    getLeaveTypeColor,
    getLeaveTypeAbbr,
    getUsagePercentage,
    getUsageColor,
    years,
    companyWorkingDays,
    refetch
  } = useTeamOverview(company?.id, selectedYear, {
    userId: userProfile?.id,
    canManageAttendance
  });

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleStatusChange = useCallback((value) => {
    setStatusFilter(value);
  }, []);

  const handleYearChange = useCallback((value) => {
    setSelectedYear(Number(value));
  }, []);

  const handleEmploymentTypeChange = useCallback((value) => {
    setEmploymentTypeFilter(value);
  }, []);

  const handleRowClick = useCallback((memberId) => {
    navigate(`/attendance/leave-profile/${memberId}`);
  }, [navigate]);

  const filteredMembers = useMemo(() => {
    let filtered = [...teamMembers];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(m =>
        m.first_name.toLowerCase().includes(term) ||
        m.last_name.toLowerCase().includes(term) ||
        m.email.toLowerCase().includes(term)
      );
    }

    if (statusFilter === 'on-leave') {
      filtered = filtered.filter(m => m.on_leave_today);
    } else if (statusFilter === 'available') {
      filtered = filtered.filter(m => !m.on_leave_today);
    }

    if (employmentTypeFilter !== 'all') {
      filtered = filtered.filter(m => m.employment_type_id === employmentTypeFilter);
    }

    return filtered;
  }, [teamMembers, searchTerm, statusFilter, employmentTypeFilter]);

  // Loading state
  if (loading && teamMembers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
          <motion.div
            className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
        <p className="text-sm text-slate-500 font-medium">Loading team data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="rounded-2xl bg-white border border-slate-200 p-8"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Team Overview</h1>
            <p className="text-slate-500 mt-1">Manage team attendance for {selectedYear}</p>
          </div>
          <div className="p-3 rounded-2xl bg-slate-50 border border-slate-100">
            <Users className="h-6 w-6 text-slate-600" />
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatPill
            icon={Users}
            label="Team Members"
            value={stats.total_members}
            color="slate"
            delay={0.1}
          />
          <StatPill
            icon={UserX}
            label="On Leave Today"
            value={stats.on_leave_today}
            color={stats.on_leave_today > 0 ? "amber" : "emerald"}
            delay={0.15}
          />
          <StatPill
            icon={UserCheck}
            label="Available"
            value={stats.available}
            color="emerald"
            delay={0.2}
          />
          <StatPill
            icon={Clock}
            label="Pending Requests"
            value={stats.with_pending}
            color={stats.with_pending > 0 ? "amber" : "slate"}
            delay={0.25}
          />
        </div>
      </motion.div>

      {/* Team Members Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        {/* Header & Filters */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Team Members</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {filteredMembers.length} {filteredMembers.length === 1 ? 'member' : 'members'}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search team members..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-9 h-11 rounded-full border-slate-200 bg-slate-50 focus:bg-white transition-colors"
              />
            </div>

            <div className="flex gap-3 flex-wrap">
              <Select value={statusFilter} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-[160px] rounded-full border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  <SelectItem value="on-leave">On Leave Today</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                </SelectContent>
              </Select>

              <Select value={employmentTypeFilter} onValueChange={handleEmploymentTypeChange}>
                <SelectTrigger className="w-[180px] rounded-full border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
                  <SelectValue placeholder="Employment type">
                    {employmentTypeFilter === 'all' ? (
                      <span className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        All Types
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: getTypeById(employmentTypeFilter)?.color }}
                        />
                        {getTypeById(employmentTypeFilter)?.name}
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-slate-500" />
                      All Types
                    </span>
                  </SelectItem>
                  {employmentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <span className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: type.color }}
                        />
                        {type.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedYear.toString()} onValueChange={handleYearChange}>
                <SelectTrigger className="w-[100px] rounded-full border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year.value} value={year.value.toString()}>
                      {year.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Team List */}
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-20 bg-slate-50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredMembers.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="text-center py-16"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 mb-4">
              <Users className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-[15px] font-semibold text-slate-700">No team members found</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your search or filters</p>
          </motion.div>
        ) : (
          <div className="space-y-2">
            {filteredMembers.map((member, index) => (
              <TeamMemberRow
                key={member.id}
                member={member}
                employmentType={getTypeById(member.employment_type_id)}
                getUsagePercentage={getUsagePercentage}
                getUsageColor={getUsageColor}
                canManageAttendance={canManageAttendance}
                onEdit={setEditingMember}
                onView={handleRowClick}
                delay={0.02 * Math.min(index, 10)}
              />
            ))}
          </div>
        )}
      </motion.div>

      {/* Quick Insights - Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Off This Week */}
        <InsightCard
          title="Off This Week"
          subtitle="Team members on leave this week"
          icon={UserX}
          iconColor="bg-amber-50 text-amber-600"
          delay={0.2}
        >
          {offThisWeek.users.length === 0 ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 mb-3">
                <UserCheck className="h-5 w-5 text-emerald-600" />
              </div>
              <p className="text-sm font-medium text-slate-600">Everyone's available this week</p>
            </div>
          ) : (
            <div className="space-y-2">
              {offThisWeek.users.slice(0, 5).map(({ user, requests }) => (
                <div key={user.id} className="group relative bg-white rounded-xl border border-slate-200/80 p-3 pl-4 transition-all duration-200 hover:shadow-sm hover:border-slate-300/80">
                  <div className="absolute left-0 top-2 bottom-2 w-1 bg-amber-500 rounded-full" />
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold text-xs">
                        {user.first_name[0]}{user.last_name[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{user.first_name} {user.last_name}</p>
                      <p className="text-xs text-slate-500">{requests.length} request{requests.length > 1 ? 's' : ''} this week</p>
                    </div>
                    <div className="flex gap-1">
                      {requests.slice(0, 2).map(req => (
                        <span key={req.id} className={cn("px-1.5 py-0.5 rounded text-[10px] font-semibold", getLeaveTypeColor(req.leave_type, req.user_id))}>
                          {getLeaveTypeAbbr(req.leave_type, req.user_id)}
                        </span>
                      ))}
                      {requests.length > 2 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                          +{requests.length - 2}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {offThisWeek.users.length > 5 && (
                <p className="text-xs text-center text-slate-500 pt-2">
                  +{offThisWeek.users.length - 5} more people off this week
                </p>
              )}
            </div>
          )}
        </InsightCard>

        {/* Upcoming Leave */}
        <InsightCard
          title="Upcoming Leave"
          subtitle="Leave starting in the next 2 weeks"
          icon={CalendarDays}
          iconColor="bg-sky-50 text-sky-600"
          delay={0.25}
        >
          {upcomingLeave.length === 0 ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 mb-3">
                <Calendar className="h-5 w-5 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-600">No upcoming leave in the next 2 weeks</p>
            </div>
          ) : (
            <div className="space-y-2">
              {upcomingLeave.slice(0, 5).map(req => (
                <LeavePreviewItem
                  key={req.id}
                  request={req}
                  getLeaveTypeColor={getLeaveTypeColor}
                  getLeaveTypeAbbr={getLeaveTypeAbbr}
                  showDaysUntil
                />
              ))}
              {upcomingLeave.length > 5 && (
                <p className="text-xs text-center text-slate-500 pt-2">
                  +{upcomingLeave.length - 5} more upcoming
                </p>
              )}
            </div>
          )}
        </InsightCard>
      </div>

      {/* Quick Insights - Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* This Month's Leave */}
        <InsightCard
          title="This Month's Leave"
          subtitle={new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          icon={Calendar}
          iconColor="bg-violet-50 text-violet-600"
          delay={0.3}
        >
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 rounded-xl bg-violet-50/50 border border-violet-100 text-center">
              <p className="text-xl font-bold text-violet-700">{thisMonthLeave.requests.length}</p>
              <p className="text-[10px] font-medium text-violet-600 uppercase tracking-wide">Requests</p>
            </div>
            <div className="p-3 rounded-xl bg-violet-50/50 border border-violet-100 text-center">
              <p className="text-xl font-bold text-violet-700">{thisMonthLeave.uniquePeople}</p>
              <p className="text-[10px] font-medium text-violet-600 uppercase tracking-wide">People</p>
            </div>
            <div className="p-3 rounded-xl bg-violet-50/50 border border-violet-100 text-center">
              <p className="text-xl font-bold text-violet-700">{thisMonthLeave.totalDays}</p>
              <p className="text-[10px] font-medium text-violet-600 uppercase tracking-wide">Days</p>
            </div>
          </div>

          {thisMonthLeave.requests.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-2">No leave this month</p>
          ) : (
            <div className="space-y-2">
              {thisMonthLeave.requests.slice(0, 3).map(req => (
                <LeavePreviewItem
                  key={req.id}
                  request={req}
                  getLeaveTypeColor={getLeaveTypeColor}
                  getLeaveTypeAbbr={getLeaveTypeAbbr}
                />
              ))}
              {thisMonthLeave.requests.length > 3 && (
                <p className="text-xs text-center text-slate-500 pt-1">
                  +{thisMonthLeave.requests.length - 3} more requests
                </p>
              )}
            </div>
          )}
        </InsightCard>

        {/* Next Month's Leave */}
        <InsightCard
          title="Next Month's Leave"
          subtitle={new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          icon={Calendar}
          iconColor="bg-emerald-50 text-emerald-600"
          delay={0.35}
        >
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 rounded-xl bg-emerald-50/50 border border-emerald-100 text-center">
              <p className="text-xl font-bold text-emerald-700">{nextMonthLeave.requests.length}</p>
              <p className="text-[10px] font-medium text-emerald-600 uppercase tracking-wide">Requests</p>
            </div>
            <div className="p-3 rounded-xl bg-emerald-50/50 border border-emerald-100 text-center">
              <p className="text-xl font-bold text-emerald-700">{nextMonthLeave.uniquePeople}</p>
              <p className="text-[10px] font-medium text-emerald-600 uppercase tracking-wide">People</p>
            </div>
            <div className="p-3 rounded-xl bg-emerald-50/50 border border-emerald-100 text-center">
              <p className="text-xl font-bold text-emerald-700">{nextMonthLeave.totalDays}</p>
              <p className="text-[10px] font-medium text-emerald-600 uppercase tracking-wide">Days</p>
            </div>
          </div>

          {nextMonthLeave.requests.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-2">No leave scheduled yet</p>
          ) : (
            <div className="space-y-2">
              {nextMonthLeave.requests.slice(0, 3).map(req => (
                <LeavePreviewItem
                  key={req.id}
                  request={req}
                  getLeaveTypeColor={getLeaveTypeColor}
                  getLeaveTypeAbbr={getLeaveTypeAbbr}
                />
              ))}
              {nextMonthLeave.requests.length > 3 && (
                <p className="text-xs text-center text-slate-500 pt-1">
                  +{nextMonthLeave.requests.length - 3} more requests
                </p>
              )}
            </div>
          )}
        </InsightCard>
      </div>

      {/* Edit Working Days Dialog */}
      <EditWorkingDaysDialog
        member={editingMember}
        companyDefault={companyWorkingDays}
        open={!!editingMember}
        onOpenChange={(open) => !open && setEditingMember(null)}
        onSaved={() => refetch()}
      />
    </div>
  );
};
